import { useOrg } from '@/organization/useOrg';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BuildingAlternative,
  SubprojectBuildingDetails,
  useSubprojectStore,
} from '../../stores/SubprojectStore.ts';
import { DecarbFooterBar } from '../components/DecarbFooterBar.tsx';
import { DecarbPageLayout } from '../components/DecarbPageLayout.tsx';
import { DecarbTabsPanel } from '../components/DecarbTabsPanel.tsx';
import { usePencilsDown } from '../hooks/usePencilsDown';
import {
  useElectricRatesQuery,
  useGasRatesQuery,
  useOnsiteEnergyQuery,
} from '../queries/energy';
import { safelySaveSubproject } from '../saveHelpers.ts';
import { EnergyRateCard, Tab } from '../types';
import {
  createEmptyBuildingAlternative,
  energySelectionsMatch,
  ensureBuildingAlternativeComplete,
  storedEnergyCosts,
  syncBuildingParamsAcrossAlternatives,
} from '../utils.ts';
import { EnergyForBuildingDetails } from './EnergyForBuildingDetails.tsx';
import { EnergyHelperText } from './EnergyHelperText.tsx';

// Every alternative complete (onSiteEnergy, externalEnergy, ... present), on a
// clone -- applied both to the load and to every save result adopted, so the
// two are shaped alike.
const withEnergyDefaults = (
  subproject: SubprojectBuildingDetails,
): SubprojectBuildingDetails => {
  const updated = structuredClone(subproject);
  updated.conventionalAlternative = ensureBuildingAlternativeComplete(
    updated.conventionalAlternative,
  );
  updated.reducedCarbonAlternative = ensureBuildingAlternativeComplete(
    updated.reducedCarbonAlternative,
  );
  updated.zeroCarbonAlternative = ensureBuildingAlternativeComplete(
    updated.zeroCarbonAlternative,
  );
  return updated;
};

export const EnergySelection = () => {
  const queryClient = useQueryClient();
  const subprojectStore = useSubprojectStore();
  const { data: onsiteData = [], isSuccess: onsiteLoaded } =
    useOnsiteEnergyQuery();
  const { subId, projectId } = useParams();
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  const [tab, setTab] = useState<Tab>('conventionalAlternative');
  const { selectedOrg } = useOrg();
  const pencilsDown = usePencilsDown();

  // Rate plan queries — projectId and subId come from URL params (always available)
  const { data: electricRates = [], isSuccess: electricLoaded } =
    useElectricRatesQuery(selectedOrg?.id, projectId, subId);
  const { data: gasRates = [], isSuccess: gasLoaded } = useGasRatesQuery(
    selectedOrg?.id,
    projectId,
    subId,
  );
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [, setIsSaving] = useState(false);
  // The copy last handed to us by the server (on load, or as a save's result).
  // Identity against `subproject` says whether there is anything to save;
  // `costsArePending` says whether the stored cost figures still describe the
  // energy selections on screen. Same pattern as HVACSelection.
  const [syncedSubproject, setSyncedSubproject] =
    useState<SubprojectBuildingDetails>();
  const costsArePending = !energySelectionsMatch(subproject, syncedSubproject);
  // Incremented as each save request is issued, so a response can tell
  // whether it is still the newest one outstanding.
  const saveSeqRef = useRef(0);

  /**
   * Adopt what the backend handed back from a save: the stored costs only
   * exist after the server has run. Ignored if a newer save has been issued
   * since; the on-screen state is replaced only if no edit was made while the
   * save was in flight. See HVACSelection for the full reasoning.
   */
  const adoptSaveResult = useCallback(
    (
      savedSnapshot: SubprojectBuildingDetails,
      result: SubprojectBuildingDetails,
      seq: number,
    ) => {
      if (seq !== saveSeqRef.current) return;
      const refreshed = withEnergyDefaults(result);
      setSyncedSubproject(refreshed);
      setSubproject((current) =>
        current === savedSnapshot ? refreshed : current,
      );
    },
    [],
  );
  // A debounced auto-save is scheduled but not yet sent. Read by the unmount
  // flush below; the per-change effect cleanup only reschedules.
  const pendingSaveRef = useRef(false);
  // The state produced by a reference-data clean-up of an UNEDITED load. It is
  // shown as pending ("--": the stored figures describe the selections before
  // the clean-up) but not auto-saved on its own -- a page load never deletes
  // selections by itself; the removal is persisted with the user's next edit.
  const cleanupOnlyRef = useRef<SubprojectBuildingDetails | undefined>(
    undefined,
  );
  const syncedSubprojectRef = useRef<SubprojectBuildingDetails | undefined>(
    undefined,
  );
  useEffect(() => {
    syncedSubprojectRef.current = syncedSubproject;
  }, [syncedSubproject]);
  const loadedSubIdRef = useRef<string | null>(null);
  const subprojectRef = useRef<SubprojectBuildingDetails | undefined>(
    undefined,
  );

  // Keep subprojectRef in sync with state so the load effect can read latest
  useEffect(() => {
    subprojectRef.current = subproject;
  }, [subproject]);

  // Auto-save function with debounce
  const saveSubproject = useCallback(() => {
    if (pencilsDown) return;
    // Nothing has changed since the server last spoke: covers the initial load
    // and a state adopted from a save result (saving that would loop).
    if (subproject && subproject !== syncedSubproject) {
      setIsSaving(true);
      // Sync building params before saving to ensure all alternatives have the same measurements
      const withSyncedParams = syncBuildingParamsAcrossAlternatives(subproject);

      // The subproject this state belongs to -- not necessarily the URL's, if
      // the user has just switched subproject (the flush on switch uses this).
      const targetId = subproject.id ?? subId;
      if (targetId && selectedOrg) {
        const savedSnapshot = subproject;
        const seq = ++saveSeqRef.current;
        safelySaveSubproject(
          targetId,
          selectedOrg.id,
          withSyncedParams,
          queryClient,
        )
          .then((result) => {
            adoptSaveResult(savedSnapshot, result, seq);
            setIsSaving(false);
          })
          .catch((error) => {
            setIsSaving(false);
            // Still the newest save: re-arm so leaving the page retries it.
            if (seq === saveSeqRef.current) pendingSaveRef.current = true;
            console.error('Failed to auto-save energy data:', error);
          });
      }
    }
  }, [
    subproject,
    syncedSubproject,
    subId,
    selectedOrg,
    queryClient,
    pencilsDown,
    adoptSaveResult,
  ]);

  // The latest saveSubproject, for the unmount flush (whose effect must not
  // re-run on every change).
  const saveSubprojectRef = useRef(saveSubproject);
  useEffect(() => {
    saveSubprojectRef.current = saveSubproject;
  }, [saveSubproject]);

  // Debounced auto-save effect - saves 2 seconds after the last change.
  useEffect(() => {
    if (pencilsDown) return;
    if (!subproject) return;

    // Nothing to save: the initial load, a state adopted from a save result, or
    // a load-time clean-up nobody has edited yet (see cleanupOnlyRef).
    if (
      subproject === syncedSubproject ||
      subproject === cleanupOnlyRef.current
    ) {
      pendingSaveRef.current = false;
      return;
    }

    // (Re)start the debounce: one save, 2 s after the last change.
    pendingSaveRef.current = true;
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      pendingSaveRef.current = false;
      saveSubproject();
    }, 2000);

    // Cleanup only cancels. It runs on every dependency change -- including each
    // adopted save result -- so it must NOT save: flushing here sent one edit
    // three or four times. The unmount flush below owns "save before leaving".
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [pencilsDown, subproject, syncedSubproject, saveSubproject]);

  // Unmount only: send a save that was scheduled but not yet sent, so leaving
  // the page within the debounce window does not lose the edit.
  useEffect(
    () => () => {
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        saveSubprojectRef.current();
      }
    },
    [],
  );

  useEffect(() => {
    if (!subId || !selectedOrg) {
      return;
    }

    // Each list gates only its own filter, and only once its query has
    // succeeded: filtering against a list that has not loaded would strip every
    // selection of that kind (and, since a removal is now saved, persist it).

    // Build valid key sets for external param validation
    const validElectricKeys = new Set(
      electricRates.map(
        (c) => `${c.utilityIdentifier}:electric:${c.state}:${c.sector}`,
      ),
    );
    const validGasKeys = new Set(
      gasRates.map((c) => `${c.utilityIdentifier}:gas:${c.state}:${c.sector}`),
    );

    // Clean up invalid energy references against reference data
    const cleanupAlternative = (alt: BuildingAlternative) => {
      // Remove onsite units that don't exist
      if (onsiteLoaded && alt.onSiteEnergy?.params) {
        alt.onSiteEnergy.params = alt.onSiteEnergy.params.filter((p) => {
          const exists = onsiteData.find((e) => e.id === p.techId);
          if (!exists) {
            console.warn(`Removing invalid onsite unit reference: ${p.techId}`);
          }
          return exists;
        });
      }
      // Remove external params whose rate card no longer exists in the territory
      if (alt.externalEnergy?.params) {
        alt.externalEnergy.params = alt.externalEnergy.params.filter((p) => {
          const isElectric = p.ratePlanType === 'electric';
          if (isElectric ? !electricLoaded : !gasLoaded) return true;
          const key = `${p.utilityIdentifier}:${p.ratePlanType}:${p.state}:${p.sector}`;
          const validKeys = isElectric ? validElectricKeys : validGasKeys;
          const exists = validKeys.has(key);
          if (!exists) {
            console.warn(`Removing invalid external rate reference: ${key}`);
          }
          return exists;
        });
      }
    };

    // If subId changed, fetch from server
    if (loadedSubIdRef.current !== subId) {
      if (loadedSubIdRef.current !== null) {
        // Switching subproject while mounted: send the previous one's scheduled
        // save now (it targets its own id), and make sure no late result from
        // it is adopted into the next one.
        if (pendingSaveRef.current) {
          if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
            autoSaveTimeoutRef.current = null;
          }
          pendingSaveRef.current = false;
          saveSubprojectRef.current();
        }
        saveSeqRef.current += 1;
        cleanupOnlyRef.current = undefined;
        setSubproject(undefined);
        setSyncedSubproject(undefined);
      }
      subprojectStore
        .fetchSubprojectDetails(subId, selectedOrg.id)
        .then((fetched) => {
          // Ensure alternatives are initialized and have all required fields.
          // `baseline` is exactly what the server's stored costs describe.
          const baseline = withEnergyDefaults(fetched);
          const cleaned = structuredClone(baseline);
          cleanupAlternative(cleaned.conventionalAlternative);
          cleanupAlternative(cleaned.reducedCarbonAlternative);
          cleanupAlternative(cleaned.zeroCarbonAlternative);

          loadedSubIdRef.current = subId;
          // The SERVER's copy is the synced baseline, not the cleaned one: its
          // stored figures were computed for the selections before clean-up.
          // If the clean-up removed anything, the screen is therefore pending
          // ("--") and the removal is saved and re-priced; if it removed
          // nothing, the baseline itself is shown and nothing is saved.
          const removedSomething = !energySelectionsMatch(cleaned, baseline);
          cleanupOnlyRef.current = removedSomething ? cleaned : undefined;
          setSubproject(removedSomething ? cleaned : baseline);
          setSyncedSubproject(baseline);
        })
        .catch(console.error);
    } else if (subprojectRef.current) {
      // Re-run triggered by onsiteData/externalData arriving after initial load.
      // Re-apply cleanup to existing subproject state without re-fetching.
      const current = subprojectRef.current;

      // Deep clone the alternatives to apply cleanup
      const conv = {
        ...current.conventionalAlternative,
        onSiteEnergy: {
          ...current.conventionalAlternative.onSiteEnergy,
          params: [...current.conventionalAlternative.onSiteEnergy.params],
        },
        externalEnergy: {
          ...current.conventionalAlternative.externalEnergy,
          params: [...current.conventionalAlternative.externalEnergy.params],
        },
      };
      const reduced = {
        ...current.reducedCarbonAlternative,
        onSiteEnergy: {
          ...current.reducedCarbonAlternative.onSiteEnergy,
          params: [...current.reducedCarbonAlternative.onSiteEnergy.params],
        },
        externalEnergy: {
          ...current.reducedCarbonAlternative.externalEnergy,
          params: [...current.reducedCarbonAlternative.externalEnergy.params],
        },
      };
      const zero = {
        ...current.zeroCarbonAlternative,
        onSiteEnergy: {
          ...current.zeroCarbonAlternative.onSiteEnergy,
          params: [...current.zeroCarbonAlternative.onSiteEnergy.params],
        },
        externalEnergy: {
          ...current.zeroCarbonAlternative.externalEnergy,
          params: [...current.zeroCarbonAlternative.externalEnergy.params],
        },
      };

      // Count params before cleanup
      const beforeCount =
        conv.onSiteEnergy.params.length +
        conv.externalEnergy.params.length +
        reduced.onSiteEnergy.params.length +
        reduced.externalEnergy.params.length +
        zero.onSiteEnergy.params.length +
        zero.externalEnergy.params.length;

      cleanupAlternative(conv);
      cleanupAlternative(reduced);
      cleanupAlternative(zero);

      // Count params after cleanup
      const afterCount =
        conv.onSiteEnergy.params.length +
        conv.externalEnergy.params.length +
        reduced.onSiteEnergy.params.length +
        reduced.externalEnergy.params.length +
        zero.onSiteEnergy.params.length +
        zero.externalEnergy.params.length;

      // Only update state if cleanup actually removed something, to avoid
      // triggering auto-save unnecessarily
      if (afterCount !== beforeCount) {
        const next = {
          ...current,
          conventionalAlternative: conv,
          reducedCarbonAlternative: reduced,
          zeroCarbonAlternative: zero,
        };
        // Cleaning an unedited load is not an edit (not auto-saved); cleaning a
        // state the user has edited rides along with their pending save.
        const unedited =
          current === syncedSubprojectRef.current ||
          current === cleanupOnlyRef.current;
        cleanupOnlyRef.current = unedited ? next : undefined;
        setSubproject(next);
      }
    }
  }, [
    subId,
    selectedOrg,
    onsiteData,
    electricRates,
    gasRates,
    onsiteLoaded,
    electricLoaded,
    gasLoaded,
    subprojectStore,
  ]);

  // Handle adding onsite energy unit
  const handleAddOnsiteEnergy = useCallback(
    (techId: string) => {
      setSubproject((prevSubproject) => {
        if (!prevSubproject) return prevSubproject;

        const alt = prevSubproject[tab];
        const params = alt.onSiteEnergy.params ?? [];

        // Check if already exists
        if (params.find((p) => p.techId === techId)) {
          return prevSubproject;
        }

        return {
          ...prevSubproject,
          [tab]: {
            ...alt,
            onSiteEnergy: {
              ...alt.onSiteEnergy,
              params: [...params, { techId, units: 1 }],
            },
          },
        };
      });
    },
    [tab],
  );

  // Handle adding external energy provider
  const handleAddExternalEnergy = useCallback(
    (card: EnergyRateCard) => {
      setSubproject((prevSubproject) => {
        if (!prevSubproject) return prevSubproject;

        const alt = prevSubproject[tab];
        const params = alt.externalEnergy.params ?? [];

        // Check if already selected (by natural key)
        const alreadySelected = params.find(
          (p) =>
            p.utilityIdentifier === card.utilityIdentifier &&
            p.ratePlanType === card.ratePlanType &&
            p.state === card.state &&
            p.sector === card.sector,
        );
        if (alreadySelected) return prevSubproject;

        return {
          ...prevSubproject,
          [tab]: {
            ...alt,
            externalEnergy: {
              ...alt.externalEnergy,
              params: [
                ...params,
                {
                  utilityIdentifier: card.utilityIdentifier,
                  ratePlanType: card.ratePlanType,
                  state: card.state,
                  sector: card.sector,
                  MWh: 0,
                },
              ],
            },
          },
        };
      });
    },
    [tab],
  );

  // Handle removing onsite energy unit
  const handleRemoveOnsiteEnergy = useCallback(
    (techId: string) => {
      setSubproject((prevSubproject) => {
        if (!prevSubproject) return prevSubproject;

        const alt = prevSubproject[tab];
        const filteredParams = alt.onSiteEnergy.params.filter(
          (p) => p.techId !== techId,
        );

        return {
          ...prevSubproject,
          [tab]: {
            ...alt,
            onSiteEnergy: {
              ...alt.onSiteEnergy,
              params: filteredParams,
            },
          },
        };
      });
    },
    [tab],
  );

  // Handle removing external energy provider (key = `utilityIdentifier:ratePlanType:state:sector`)
  const handleRemoveExternalEnergy = useCallback(
    (key: string) => {
      setSubproject((prevSubproject) => {
        if (!prevSubproject) return prevSubproject;

        const alt = prevSubproject[tab];
        const filteredParams = alt.externalEnergy.params.filter(
          (p) =>
            `${p.utilityIdentifier}:${p.ratePlanType}:${p.state}:${p.sector}` !==
            key,
        );

        return {
          ...prevSubproject,
          [tab]: {
            ...alt,
            externalEnergy: {
              ...alt.externalEnergy,
              params: filteredParams,
            },
          },
        };
      });
    },
    [tab],
  );

  // Handle changing onsite energy unit count
  const handleChangeOnsiteEnergyCount = useCallback(
    (techId: string, newCount: number) => {
      setSubproject((prevSubproject) => {
        if (!prevSubproject) return prevSubproject;

        const alt = prevSubproject[tab];
        let updatedParams;

        if (newCount > 0) {
          updatedParams = alt.onSiteEnergy.params.map((p) =>
            p.techId === techId ? { ...p, units: newCount } : p,
          );
        } else {
          updatedParams = alt.onSiteEnergy.params.filter(
            (p) => p.techId !== techId,
          );
        }

        return {
          ...prevSubproject,
          [tab]: {
            ...alt,
            onSiteEnergy: {
              ...alt.onSiteEnergy,
              params: updatedParams,
            },
          },
        };
      });
    },
    [tab],
  );

  // Copy conventional energy selections to reduced carbon, filtering out gas-based sources
  const handleCopyConventionalToReduced = useCallback(() => {
    setSubproject((prevSubproject) => {
      if (!prevSubproject) return prevSubproject;

      const convAlt = prevSubproject.conventionalAlternative;

      // Keep only electric rate params for the Low Carbon alternative
      const filteredExternalParams = (
        convAlt.externalEnergy.params ?? []
      ).filter((p) => p.ratePlanType === 'electric');

      // Filter onsite energy (filter by energySource, exclude gas-based)
      const gasTypes = ['Natural Gas', 'Propane', 'Diesel', 'Electricity/ Gas'];
      const isGasSource = (source: string) =>
        gasTypes.some((gasType) => source.includes(gasType));

      const filteredOnsiteParams = (convAlt.onSiteEnergy.params ?? []).filter(
        (param) => {
          const provider = onsiteData.find((e) => e.id === param.techId);
          return provider && !isGasSource(provider.energySource);
        },
      );

      const reducedAlt = prevSubproject.reducedCarbonAlternative;
      return {
        ...prevSubproject,
        reducedCarbonAlternative: {
          ...reducedAlt,
          externalEnergy: {
            ...reducedAlt.externalEnergy,
            params: filteredExternalParams,
          },
          onSiteEnergy: {
            ...reducedAlt.onSiteEnergy,
            params: filteredOnsiteParams,
          },
        },
      };
    });
  }, [onsiteData]);

  // Copy reduced carbon energy selections to zero carbon
  const handleCopyReducedToZero = useCallback(() => {
    setSubproject((prevSubproject) => {
      if (!prevSubproject) return prevSubproject;

      const zeroAlt =
        prevSubproject.zeroCarbonAlternative ??
        createEmptyBuildingAlternative();
      const reducedAlt = prevSubproject.reducedCarbonAlternative;

      return {
        ...prevSubproject,
        zeroCarbonAlternative: {
          ...zeroAlt,
          externalEnergy: reducedAlt?.externalEnergy
            ? {
                ...reducedAlt.externalEnergy,
                params: [...reducedAlt.externalEnergy.params],
              }
            : zeroAlt.externalEnergy,
          onSiteEnergy: reducedAlt?.onSiteEnergy
            ? {
                ...reducedAlt.onSiteEnergy,
                params: [...reducedAlt.onSiteEnergy.params],
              }
            : zeroAlt.onSiteEnergy,
        },
      };
    });
  }, []);

  // Compute metrics for DecarbTabsPanel (capital cost + total energy demand in MWh)
  const getEnergyMetrics = (
    tabKey: Tab,
  ): { cost?: number; energyDemand: number } | undefined => {
    const alternative = subproject?.[tabKey];
    if (!alternative) return undefined;
    const hasEnergySelected = Boolean(
      alternative?.onSiteEnergy?.params?.length ||
      alternative?.externalEnergy?.params?.length,
    );
    if (!hasEnergySelected) return undefined;
    // Sum all energy demand values (MWh) from the alternative's energyDemand record
    const totalEnergyDemand = Object.values(
      alternative.energyDemand || {},
    ).reduce((sum, val) => sum + (val || 0), 0);
    return {
      // Stored on-site capital, never local arithmetic -- see storedEnergyCosts.
      // The CAPITAL COST tile (EnergyForBuildingDetails) reads the same figure.
      cost: storedEnergyCosts(alternative, costsArePending).capitalCost,
      energyDemand: totalEnergyDemand,
    };
  };

  const conventionalMetrics = useMemo(
    () => getEnergyMetrics('conventionalAlternative'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subproject, costsArePending],
  );
  const reducedCarbonMetrics = useMemo(
    () => getEnergyMetrics('reducedCarbonAlternative'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subproject, costsArePending],
  );
  const zeroCarbonMetrics = useMemo(
    () => getEnergyMetrics('zeroCarbonAlternative'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subproject, costsArePending],
  );

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  return (
    <DecarbPageLayout
      title={'Select Energy Sources'}
      tabs={
        <DecarbTabsPanel
          currentTab={tab}
          onClick={setTab}
          conventionalMetrics={conventionalMetrics}
          reducedCarbonMetrics={reducedCarbonMetrics}
          zeroCarbonMetrics={zeroCarbonMetrics}
        />
      }
      footer={
        <DecarbFooterBar
          currentTab={tab}
          changeTab={setTab}
          saveSubproject={(onSuccessFn: () => void) => {
            if (pencilsDown) {
              onSuccessFn();
              return;
            }
            if (subproject && subId && selectedOrg) {
              // This save supersedes a scheduled auto-save (and the unmount
              // flush that would otherwise follow the page change).
              if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = null;
              }
              pendingSaveRef.current = false;
              const withSyncedParams =
                syncBuildingParamsAcrossAlternatives(subproject);
              const savedSnapshot = subproject;
              const seq = ++saveSeqRef.current;
              safelySaveSubproject(
                subId,
                selectedOrg.id,
                withSyncedParams,
                queryClient,
              )
                .then((result) => {
                  adoptSaveResult(savedSnapshot, result, seq);
                  onSuccessFn();
                })
                .catch((error) => {
                  // Re-arm: leaving the page (or the next change) retries it.
                  if (seq === saveSeqRef.current) pendingSaveRef.current = true;
                  console.error('Failed to save energy data:', error);
                  alert('Failed to save changes. Please try again.');
                });
            }
          }}
        />
      }
    >
      {subproject && tab === 'conventionalAlternative' && (
        <EnergyForBuildingDetails
          building={subproject}
          costsArePending={costsArePending}
          alternative={subproject.conventionalAlternative}
          onAddOnsiteEnergy={handleAddOnsiteEnergy}
          onAddExternalEnergy={handleAddExternalEnergy}
          onRemoveOnsiteEnergy={handleRemoveOnsiteEnergy}
          onRemoveExternalEnergy={handleRemoveExternalEnergy}
          onChangeOnsiteEnergyCount={handleChangeOnsiteEnergyCount}
          readOnly={pencilsDown}
          emptyText={<EnergyHelperText tab={tab} />}
        />
      )}
      {subproject && tab === 'reducedCarbonAlternative' && (
        <EnergyForBuildingDetails
          building={subproject}
          costsArePending={costsArePending}
          alternative={subproject.reducedCarbonAlternative}
          onAddOnsiteEnergy={handleAddOnsiteEnergy}
          onAddExternalEnergy={handleAddExternalEnergy}
          onRemoveOnsiteEnergy={handleRemoveOnsiteEnergy}
          onRemoveExternalEnergy={handleRemoveExternalEnergy}
          onChangeOnsiteEnergyCount={handleChangeOnsiteEnergyCount}
          readOnly={pencilsDown}
          emptyText={
            <EnergyHelperText
              tab={tab}
              onCopyFromConventional={
                pencilsDown ? undefined : handleCopyConventionalToReduced
              }
            />
          }
        />
      )}
      {subproject && tab === 'zeroCarbonAlternative' && (
        <EnergyForBuildingDetails
          building={subproject}
          costsArePending={costsArePending}
          alternative={subproject.zeroCarbonAlternative}
          onAddOnsiteEnergy={handleAddOnsiteEnergy}
          onAddExternalEnergy={handleAddExternalEnergy}
          onRemoveOnsiteEnergy={handleRemoveOnsiteEnergy}
          onRemoveExternalEnergy={handleRemoveExternalEnergy}
          onChangeOnsiteEnergyCount={handleChangeOnsiteEnergyCount}
          readOnly={pencilsDown}
          emptyText={
            <EnergyHelperText
              tab={tab}
              onCopyFromReduced={
                pencilsDown ? undefined : handleCopyReducedToZero
              }
            />
          }
        />
      )}
    </DecarbPageLayout>
  );
};
